import { type Lesson } from "@core/grades/types";
import { type LessonFilter } from "@core/home/types";
import { useEffect, useState } from "react";
import { parseError, URLS } from "../../../core/shared/utils";
import { useAction } from "../../../framework/remix/shared/hooks";

interface UseLessonsByFilterProps {
  filter: LessonFilter;
  enabled?: boolean;
}

interface UseLessonsByFilterReturn {
  lessons: Lesson[];
  isLoading: boolean;
  error: string | null;
}

export function useLessonsByFilter({
  filter,
  enabled = true,
}: UseLessonsByFilterProps): UseLessonsByFilterReturn {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const action = useAction(URLS.lessons.list);

  useEffect(() => {
    if (!enabled || !filter.subject || !filter.grade) {
      setLessons([]);
      return;
    }

    const fetchLessons = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await action(filter);

        if (response.isOK) {
          setLessons(response.data.lessons || []);
        }

        if (response.error) {
          setError(parseError(response.error));
        }
      } catch (err) {
        setError(parseError(err));
        setLessons([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLessons();
  }, [filter.subject, filter.grade, enabled]);

  return { lessons, isLoading, error };
}
